import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Bell, AlertCircle, Calendar, Eye, EyeOff } from 'lucide-react';
import { communicationsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminCommunications = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    is_published: false
  });
  const { toast } = useToast();

  useEffect(() => {
    loadCommunications();
  }, []);

  const loadCommunications = async () => {
    try {
      const res = await communicationsAPI.list(false);
      setCommunications(res.data);
    } catch (error) {
      console.error('Error loading communications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await communicationsAPI.update(editingId, formData);
        toast({ title: 'Communication mise à jour' });
      } else {
        await communicationsAPI.create(formData);
        toast({ title: 'Communication créée' });
      }
      resetForm();
      loadCommunications();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Une erreur est survenue', variant: 'destructive' });
    }
  };

  const handleEdit = (comm) => {
    setEditingId(comm.id);
    setFormData({
      title: comm.title,
      content: comm.content,
      type: comm.type,
      is_published: comm.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette communication ?')) return;
    try {
      await communicationsAPI.delete(id);
      toast({ title: 'Communication supprimée' });
      loadCommunications();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  const togglePublish = async (comm) => {
    try {
      await communicationsAPI.update(comm.id, { is_published: !comm.is_published });
      toast({ title: comm.is_published ? 'Communication dépubliée' : 'Communication publiée' });
      loadCommunications();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', content: '', type: 'info', is_published: false });
  };

  const getTypeBadge = (type) => {
    const styles = {
      info: 'bg-blue-100 text-blue-800',
      urgent: 'bg-red-100 text-red-800',
      event: 'bg-purple-100 text-purple-800'
    };
    const labels = { info: 'Information', urgent: 'Urgent', event: 'Événement' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type]}`}>
        {labels[type]}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-communications">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#2C3C65]">Communications</h1>
          <Button onClick={() => setShowForm(true)} className="bg-[#285BBB] hover:bg-[#2C3C65]">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle communication
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && resetForm()}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6"
            >
              <h2 className="text-xl font-bold text-[#2C3C65] mb-4">
                {editingId ? 'Modifier la communication' : 'Nouvelle communication'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Titre</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="comm-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Contenu</label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={4}
                    required
                    data-testid="comm-content"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full border rounded-lg p-2"
                    data-testid="comm-type"
                  >
                    <option value="info">Information</option>
                    <option value="urgent">Urgent</option>
                    <option value="event">Événement</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="h-4 w-4"
                    id="is_published"
                  />
                  <label htmlFor="is_published" className="text-sm text-[#2C3C65]">
                    Publier immédiatement
                  </label>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-[#285BBB] hover:bg-[#2C3C65]">
                    {editingId ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Communications List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
          </div>
        ) : communications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#6777A1]">Aucune communication pour le moment</p>
          </div>
        ) : (
          <div className="space-y-4">
            {communications.map((comm) => (
              <motion.div
                key={comm.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl shadow-md p-6 border-l-4 ${
                  comm.type === 'urgent' ? 'border-red-500' :
                  comm.type === 'event' ? 'border-purple-500' :
                  'border-blue-500'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-[#2C3C65]">{comm.title}</h3>
                      {getTypeBadge(comm.type)}
                      {comm.is_published ? (
                        <span className="flex items-center text-xs text-green-600">
                          <Eye className="h-3 w-3 mr-1" /> Publié
                        </span>
                      ) : (
                        <span className="flex items-center text-xs text-gray-400">
                          <EyeOff className="h-3 w-3 mr-1" /> Brouillon
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#6777A1] mb-3">{comm.content}</p>
                    <div className="flex items-center text-xs text-gray-400">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(comm.created_at).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => togglePublish(comm)}
                      className="text-gray-400 hover:text-[#285BBB]"
                    >
                      {comm.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(comm)}
                      className="text-gray-400 hover:text-[#285BBB]"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(comm.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCommunications;
